/*
 *
 * ChallengeQuestion action/reducer
 *
 */

import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { createActionDispatchers } from "utils/reduxHelpers";
import { setActivationCode, setToken } from "../redux";
import { ChallengeQuestionSubmissionResponsePayload } from "./types";

export type State = {
  loading: boolean;
  showError: boolean;
  question: string | null;
  token: string | null;
  noOfRetries: number;
  emailError: boolean;
  isInvalidCode: boolean;
  hasEmailExpired: boolean;
};

export const initialState: State = {
  loading: false,
  showError: false,
  question: null,
  token: null,
  noOfRetries: 3,
  emailError: false,
  isInvalidCode: false,
  hasEmailExpired: false,
};

const slice = createSlice({
  name: "challengeQuestion",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    setError(state: State, action: PayloadAction<boolean>) {
      state.showError = action.payload;
    },
    setEmailError(state: State, action: PayloadAction<boolean>) {
      state.emailError = action.payload;
    },
    setInvalidCode(state: State, action: PayloadAction<boolean>) {
      state.isInvalidCode = action.payload;
    },
    setEmailExpired(state: State, action: PayloadAction<boolean>) {
      state.hasEmailExpired = action.payload;
    },
    loadChallengeQuestion(state: State) {
      state.loading = true;
    },
    afterChallengeQuestionFetch(
      state: State,
      action: PayloadAction<string | null>
    ) {
      state.loading = false;
      state.question = action.payload;
    },
    onAnswerSubmission(state: State) {
      state.loading = true;
    },
    afterChallengeQuestionSubmissionResponse(
      state: State,
      action: PayloadAction<ChallengeQuestionSubmissionResponsePayload>
    ) {
      state.loading = false;
      state.token = action.payload.access_token;
    },
    setRetries(state: State) {
      state.noOfRetries -= 1;
    },
  },
});

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("challengeQuestion/pureAction");

export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  ["onAnswerSubmission", "loadChallengeQuestion"],
  // pure actions
  {
    pureAction,
    setActivationCode,
    setToken,
    goToPasswordSetup: () => push("/password-setup"),
  }
);

export const {
  setError,
  setEmailError,
  setInvalidCode,
  setEmailExpired,
  loadChallengeQuestion,
  afterChallengeQuestionFetch,
  onAnswerSubmission,
  afterChallengeQuestionSubmissionResponse,
  setRetries,
} = slice.actions;

export default slice.reducer;
